import { useEffect, useRef, useState } from 'react';

/**
 * Minimal canvas signature pad. Captures a hand-drawn signature and emits it as a
 * PNG `File` on each stroke end (or `null` when cleared). The consumer uploads the
 * file and stores the returned reference — matching the file/image answer flow.
 */
export function SignatureField({
  onCapture,
  disabled,
}: {
  onCapture: (file: File | null) => void;
  disabled?: boolean;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const hasContent = useRef(false);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111827';
  }, []);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const ctx = canvasRef.current!.getContext('2d')!;
    drawing.current = true;
    const { x, y } = point(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    canvasRef.current!.setPointerCapture(e.pointerId);
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current!.getContext('2d')!;
    const { x, y } = point(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    hasContent.current = true;
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (!hasContent.current) return;
    canvasRef.current!.toBlob((blob) => {
      if (blob) onCapture(new File([blob], 'signature.png', { type: 'image/png' }));
    }, 'image/png');
  };

  const [, force] = useState(0);
  const clear = () => {
    const canvas = canvasRef.current!;
    canvas.getContext('2d')!.clearRect(0, 0, canvas.width, canvas.height);
    hasContent.current = false;
    force((n) => n + 1);
    onCapture(null);
  };

  return (
    <div className="inline-flex flex-col gap-1">
      <canvas
        ref={canvasRef}
        width={320}
        height={140}
        className="touch-none rounded-sm border border-hairline-strong bg-white"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
      />
      <button type="button" onClick={clear} className="self-start text-caption text-primary-700">
        Clear
      </button>
    </div>
  );
}
